import React, { useState } from "react";
import {
    Search,
    Plus,
    Trash2,
    Pencil,
    Check,
    X,
} from "lucide-react";
import { GrAnnounce } from "react-icons/gr";
import { MdArrowDropDown, MdOutlineArrowDropUp } from "react-icons/md";

const SnagList = ({
    activeGlobalChat,
    setActiveGlobalChat,
    setActiveSnag,
    setTrigger,
    markMessagesAsRead,
    lastMessages,
    unreadCounts,
    showChatrooms,
    setShowChatrooms,
    showNewSnagForm,
    setShowNewSnagForm,
    isOwnerOrAdmin,
    newSnagTitle,
    setNewSnagTitle,
    handleCreateSnag,
    visibleSnags,
    activeSnag,
    deleteSnag,
    renameSnag,
}) => {
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState("");

    const startEditing = (snag) => {
        setEditingId(snag.id);
        setEditingTitle(snag.title || "");
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditingTitle("");
    };

    const commitEditing = async () => {
        if (!editingId) return;
        const title = editingTitle.trim();
        if (!title) {
            cancelEditing();
            return;
        }
        await renameSnag?.(editingId, title);
        cancelEditing();
    };

    return (
        <div className="w-80 bg-white border-r flex flex-col">
            <div className="p-1 border-b bg-white flex justify-between items-center">
                <div className="space-y-5 w-full">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-[#7A6E82]" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search in chat"
                            className="pl-10 pr-4 py-2 w-[95%] mx-1 bg-white border-b rounded-3xl text-sm text-[#877A90] focus:outline-none focus:ring-2 focus:ring-[#3B0069] focus:border-transparent transition-all"
                        />
                    </div>
                    {/* General Updates card */}
                    <div
                        className={`p-3 rounded-lg cursor-pointer hover:shadow-md transition w-full self-start ${activeGlobalChat === "general-updates"
                            ? "bg-purple-100 border-l-4 border-l-purple-600"
                            : "bg-purple-50"
                            }`}
                        onClick={() => {
                            setActiveGlobalChat("general-updates");
                            setActiveSnag(null);
                            setTrigger((prev) => prev + 1);
                            markMessagesAsRead("general-updates", true);
                        }}
                    >
                        <div className="flex justify-between items-center overflow-hidden">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                    <GrAnnounce className="w-5 h-5 text-purple-900" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-purple-900">General Updates</h3>
                                    <p className="text-xs text-gray-700 truncate">
                                        {lastMessages["general-updates"]
                                            ? `${lastMessages["general-updates"].senderName}: ${lastMessages[
                                                "general-updates"
                                            ].text.substring(0, 40)}...`
                                            : "No messages yet"}
                                    </p>
                                </div>
                            </div>
                            {unreadCounts["general-updates"] > 0 && (
                                <div className="bg-purple-900 text-white text-xs px-2 py-1 font-medium rounded-full min-w-[24px] h-6 flex items-center justify-center">
                                    {unreadCounts["general-updates"]}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Chatrooms header */}
                    <div className="flex items-center justify-between px-3 py-2">
                        <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => setShowChatrooms(!showChatrooms)}
                        >
                            {showChatrooms ? (
                                <MdOutlineArrowDropUp className="h-5 w-5 text-[#8D8D8D]" />
                            ) : (
                                <MdArrowDropDown className="h-5 w-5 text-[#8D8D8D]" />
                            )}
                            <h3 className="font-bold text-purple-900">Snag List</h3>
                        </div>
                        <button
                            onClick={() => setShowNewSnagForm(!showNewSnagForm)}
                            style={{
                                display: isOwnerOrAdmin ? "flex" : "none",
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition"
                        >
                            <Plus size={16} className="text-purple-900" />
                        </button>
                    </div>
                </div>
            </div>

            {showNewSnagForm && (
                <div className="p-3 bg-white border-b">
                    <input
                        type="text"
                        placeholder="New chatroom title..."
                        className="w-full border rounded p-2 mb-2"
                        value={newSnagTitle}
                        onChange={(e) => setNewSnagTitle(e.target.value)}
                    />
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={() => setShowNewSnagForm(false)}
                            className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateSnag}
                            className="px-2 py-1 text-sm bg-purple-700 text-white rounded hover:bg-purple-800"
                        >
                            Create
                        </button>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto">
                {visibleSnags.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                        No chatrooms yet. Create one to get started.
                    </div>
                ) : (
                    showChatrooms &&
                    visibleSnags.map((snag) => (
                        <div
                            key={snag.id}
                            onClick={() => {
                                setActiveSnag(snag.id);
                                setActiveGlobalChat(null);
                                markMessagesAsRead(snag.id, false);
                            }}
                            className={`p-3 border-b cursor-pointer ${activeSnag === snag.id
                                ? "bg-purple-50 border-l-4 border-l-purple-600"
                                : "hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0 pr-2">
                                    <>
                                        <div className="font-medium truncate">{snag.title}</div>
                                        {lastMessages[snag.id] && (
                                            <div className="text-xs text-gray-500 truncate mt-1">
                                                {lastMessages[snag.id].senderName}:{" "}
                                                {lastMessages[snag.id].text.substring(0, 30)}
                                            </div>
                                        )}
                                    </>
                                </div>
                                <div className="flex items-center shrink-0">
                                    {unreadCounts[snag.id] > 0 && (
                                        <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] h-5 flex items-center justify-center ml-2">
                                            {unreadCounts[snag.id]}
                                        </div>
                                    )}
                                    {isOwnerOrAdmin && (
                                        <div className="flex items-center ml-2">
                                            <button
                                                title="Rename chatroom"
                                                className="text-gray-500 hover:text-purple-700 p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    startEditing(snag);
                                                }}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                title="Delete chatroom"
                                                className="ml-2 text-gray-400 hover:text-red-600 p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteSnag(snag.id, snag.title);
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-2 flex justify-between items-center">
                                <span className="text-xs text-gray-500">
                                    {new Date(snag.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {/* Edit snag title modal */}
            {editingId && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
                    <div
                        className="bg-white rounded-lg shadow-lg p-4 w-80"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="font-bold text-purple-900 mb-3">Edit chatroom name</h3>
                        <input
                            type="text"
                            className="w-full border rounded px-2 py-1 text-sm mb-3"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-3 py-1 text-sm rounded border text-gray-600 hover:bg-gray-100"
                                onClick={cancelEditing}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-3 py-1 text-sm rounded bg-purple-700 text-white hover:bg-purple-800 flex items-center gap-1"
                                onClick={commitEditing}
                            >
                                <Check size={14} /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SnagList;
